import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BackLink, PageHeader } from '@/layouts/AppLayout';

const schema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  type: z.enum(['travel_agent', 'b2c', 'corporate']),
  contactPerson: z.string().max(255).optional(),
  contactEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  contactPhone: z.string().max(50).optional(),
  address: z.string().optional(),
  city: z.string().max(100).optional(),
  country: z.string().max(100).optional(),
  gstin: z.string().max(20).optional(),
  pan: z.string().max(20).optional(),
  creditDays: z.coerce.number().int().min(0).optional(),
  status: z.enum(['active', 'inactive']).optional(),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

export function CreateClientPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { country: 'India', status: 'active', creditDays: 0, type: 'travel_agent' },
  });

  const type = watch('type');
  const status = watch('status');

  const mutation = useMutation({
    mutationFn: (data: FormValues) => api.post('/clients', data),
    onSuccess: (created: { id: string }) => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client created');
      navigate(`/clients/${created.id}`);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to create client';
      setError('root', { message: msg });
    },
  });

  return (
    <div>
      <PageHeader title="New client" />
      <div className="p-6 max-w-2xl">
        <BackLink to="/clients" label="Back to clients" />
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Basic information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">Name *</Label>
                <Input id="name" {...register('name')} placeholder="Crystal India" />
                {errors.name && <p className="text-xs text-danger-700">{errors.name.message}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Type *</Label>
                  <Select value={type} onValueChange={(v) => setValue('type', v as FormValues['type'], { shouldDirty: true })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="travel_agent">Travel Agent</SelectItem>
                      <SelectItem value="b2c">B2C</SelectItem>
                      <SelectItem value="corporate">Corporate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={status} onValueChange={(v) => setValue('status', v as FormValues['status'], { shouldDirty: true })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="notes">Notes</Label>
                <Input id="notes" {...register('notes')} placeholder="Any remarks…" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Contact details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="contactPerson">Contact person</Label>
                <Input id="contactPerson" {...register('contactPerson')} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="contactEmail">Email</Label>
                  <Input id="contactEmail" type="email" {...register('contactEmail')} />
                  {errors.contactEmail && <p className="text-xs text-danger-700">{errors.contactEmail.message}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contactPhone">Phone</Label>
                  <Input id="contactPhone" {...register('contactPhone')} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="address">Address</Label>
                <Input id="address" {...register('address')} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="city">City</Label>
                  <Input id="city" {...register('city')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="country">Country</Label>
                  <Input id="country" {...register('country')} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Billing & compliance</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="gstin">GSTIN</Label>
                  <Input id="gstin" {...register('gstin')} placeholder="22AAAAA0000A1Z5" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pan">PAN</Label>
                  <Input id="pan" {...register('pan')} placeholder="AAAAA9999A" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="creditDays">Credit days</Label>
                  <Input id="creditDays" type="number" min={0} {...register('creditDays')} />
                  {errors.creditDays && <p className="text-xs text-danger-700">{errors.creditDays.message}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {errors.root && (
            <p className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">{errors.root.message}</p>
          )}
          <div className="flex gap-3">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create client'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/clients')}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
